// Simpro-style dashboard panel row: Alerts / Announcements / Reminders —
// tab-headed cards in the Fieldlink orange tactical style.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Send, X, Truck, Award, Hammer, BellRing, TriangleAlert, Megaphone, Banknote, Mail, ChevronDown, ChevronUp, ChevronRight, Plus, CalendarDays, Clock, ShieldAlert, ClipboardCheck, Zap, Sun, FileText, Boxes } from "lucide-react";
import { useForecastDays, WX_TEXT } from "@/lib/useWeather";
import { FieldWeather } from "@/components/FieldWeather";
import { toast } from "sonner";

const panelCls = "ops-card overview-panel";

function PanelHeader({ icon: Icon, title, right }) {
  return (
    <div className="flex items-center justify-between px-4 pt-4 pb-3">
      <div className="flex items-center gap-2">
        <span className={`overview-panel-icon ${title === "Announcements" ? "overview-icon-blue" : title === "Reminders" ? "overview-icon-purple" : ""}`}><Icon className="w-4 h-4" /></span>
        <span className="font-bold text-[15px]">{title}</span>
      </div>
      {right}
    </div>
  );
}

function AlertRow({ to, label, count, icon: Icon, color = "#FF5F15" }) {
  const has = count > 0;
  return (
    <Link to={to} className="grid grid-cols-[28px_32px_minmax(0,1fr)_14px] items-center gap-2.5 min-h-10 px-2.5 py-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-white/[0.04] transition-colors group" data-testid={`alert-row-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      {Icon && (
        <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={has ? { backgroundColor: `${color}1F`, color } : undefined}>
          <Icon className={`w-3.5 h-3.5 ${has ? "" : "text-zinc-400"}`} />
        </span>
      )}
      <span className={`min-w-[26px] text-center px-1.5 py-0.5 text-[11px] font-bold tabular-nums rounded-md text-white ${has ? "" : "bg-zinc-200 dark:bg-zinc-800 !text-zinc-500"}`}
            style={has ? { backgroundColor: color } : undefined}>{count ?? 0}</span>
      <span className="text-xs font-medium flex-1 min-w-0 leading-snug group-hover:text-[#FF5F15] transition-colors">{label}</span>
      <ChevronRight className="w-3.5 h-3.5 text-zinc-400 opacity-60 group-hover:opacity-100 group-hover:text-[#FF5F15] transition-opacity" />
    </Link>
  );
}

const KIND_ICON = { fleet: Truck, cert: Award, job: Hammer };

function OverdueInvoicesBanner({ invoices, onChased, onHidden }) {
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  if (!invoices || invoices.length === 0) return null;
  const totalOwed = invoices.reduce((s, i) => s + (i.outstanding || 0), 0);
  const COLLAPSED_COUNT = 5;
  const visible = expanded ? invoices : invoices.slice(0, COLLAPSED_COUNT);
  const hiddenCount = invoices.length - COLLAPSED_COUNT;

  const chase = async (inv) => {
    let to = inv.client_email;
    if (!to) {
      to = window.prompt(`No email saved for ${inv.client_name || "this customer"}.\n\nSend the payment reminder to:`);
      if (!to) return;
    }
    setBusyId(inv.id);
    try {
      const { data } = await api.post(`/customer-invoices/${inv.id}/chase`, { to_email: to });
      toast.success(`Payment reminder sent to ${data.to}`);
      onChased(inv.id, data.chased_at);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the reminder");
    } finally { setBusyId(null); }
  };

  const chasedToday = (ts) => ts && ts.slice(0, 10) === new Date().toLocaleDateString("en-CA");

  const hide = async (inv) => {
    try {
      await api.post(`/customer-invoices/${inv.id}/overview-hide`);
      toast.success("Removed from overview — it stays in Customer invoices");
      onHidden(inv.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove it");
    }
  };

  return (
    <div className="mt-8 mb-2 rounded-[14px] bg-white dark:bg-[#142030] border border-red-400/40 dark:border-red-500/25  overflow-hidden" data-testid="overdue-invoices-banner">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 pt-4 pb-2">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
            <Banknote className="w-4 h-4 text-red-500" />
          </span>
          <span className="text-[14px] font-bold">Overdue payments</span>
          <span className="bg-red-600 text-white text-[11px] font-mono font-bold px-1.5 py-0.5 rounded-md">{invoices.length}</span>
        </div>
        <span className="ml-auto font-extrabold text-2xl tracking-tight tabular-nums text-zinc-900 dark:text-white">
          ${totalOwed.toLocaleString("en-NZ", { minimumFractionDigits: 2 })}
          <span className="text-[11px] font-mono font-bold text-zinc-500 ml-2 tracking-[0.15em]">OWED</span>
        </span>
      </div>
      <div className="px-5 pb-4 divide-y divide-zinc-100 dark:divide-white/[0.05]">
        {visible.map((inv) => (
          <div key={inv.id} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`overdue-invoice-row-${inv.id}`}>
            <Link to="/app/customer-invoices" className="font-mono font-bold text-xs hover:text-[#FF5F15] transition-colors">{inv.invoice_number}</Link>
            <span className="text-xs truncate flex-1 min-w-[100px]">{inv.client_name || "—"}</span>
            <span className="text-xs font-mono font-bold">${(inv.outstanding || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2 })}</span>
            <span className="text-[11px] font-mono font-bold text-red-600 whitespace-nowrap">{inv.days_overdue}d overdue</span>
            {chasedToday(inv.last_chased_at) ? (
              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 inline-flex items-center" data-testid={`chased-today-${inv.id}`}>
                <Mail className="w-3 h-3 mr-1" /> Chased today
              </span>
            ) : (
              <button
                onClick={() => chase(inv)}
                disabled={busyId === inv.id}
                className="text-[11px] font-bold uppercase tracking-wider bg-red-600 hover:bg-red-500 text-white px-3 py-1.5 rounded-lg disabled:opacity-50 inline-flex items-center transition-colors"
                data-testid={`chase-invoice-btn-${inv.id}`}
              >
                {busyId === inv.id ? "Sending…" : "Chase now →"}
              </button>
            )}
            <button
              onClick={() => hide(inv)}
              className="text-zinc-400 hover:text-red-500 transition-colors shrink-0"
              title="Remove from overview"
              aria-label="Remove from overview"
              data-testid={`hide-overdue-btn-${inv.id}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full pt-2.5 pb-0.5 flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] transition-colors"
            data-testid="overdue-invoices-expand-btn"
          >
            {expanded ? (
              <><ChevronUp className="w-3.5 h-3.5" /> Show less</>
            ) : (
              <><ChevronDown className="w-3.5 h-3.5" /> Show all {invoices.length} ({hiddenCount} more)</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export function DashboardPanels({ stats, rail = false, overdueOnly = false }) {
  const { user } = useAuth();
  const [panel, setPanel] = useState(null);
  const [ann, setAnn] = useState(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [showAllReminders, setShowAllReminders] = useState(false);

  const loadAnn = () => api.get("/dashboard-notes").then((r) => setAnn(r.data)).catch(() => setAnn([]));

  useEffect(() => {
    api.get("/overview/panel").then((r) => setPanel(r.data)).catch(() => setPanel({ alerts: {}, reminders: [] }));
    loadAnn();
  }, []);

  const post = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      await api.post("/dashboard-notes", { text: draft.trim() });
      setDraft("");
      loadAnn();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post");
    } finally { setPosting(false); }
  };

  const removeAnn = async (id) => {
    try { await api.delete(`/dashboard-notes/${id}`); loadAnn(); } catch { toast.error("Could not delete"); }
  };

  const a = panel?.alerts || {};
  const reminders = panel?.reminders || [];
  const fmtDate = (d) => {
    try { return new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "short" }); } catch { return d; }
  };

  return (
    <>
      {overdueOnly || !rail ? (
        <OverdueInvoicesBanner
          invoices={panel?.overdue_invoices}
          onChased={(id, ts) => setPanel((p) => ({
            ...p,
            overdue_invoices: (p.overdue_invoices || []).map((i) => (i.id === id ? { ...i, last_chased_at: ts } : i)),
          }))}
          onHidden={(id) => setPanel((p) => ({
            ...p,
            overdue_invoices: (p.overdue_invoices || []).filter((i) => i.id !== id),
          }))}
        />
      ) : null}
      {overdueOnly ? null : (
      <div className={rail ? "grid gap-4 grid-cols-1" : "grid md:grid-cols-3 gap-6 mt-8 mb-2"} data-testid="dashboard-panels">
      {/* Alerts */}
      <div className={panelCls} data-testid="panel-alerts">
        <PanelHeader
          icon={Zap}
          title="Needs attention"
          right={<Link to="/app/inbox" className="text-[11px] font-bold text-[#FF5F15] hover:underline" data-testid="alerts-view-all">View all →</Link>}
        />
        <div className="px-2 pb-3 grid grid-cols-1">
          <AlertRow to="/app/safety" label="Safety items require review" count={stats?.open_safety} color="#EF4444" icon={ShieldAlert} />
          <AlertRow to="/app/timesheets" label="Timesheets pending approval" count={stats?.pending_timesheets} color="#B77905" icon={Clock} />
          <AlertRow to="/app/leave" label="Leave requests" count={stats?.pending_leave} color="#3B82F6" icon={CalendarDays} />
          <AlertRow to="/app/customer-invoices" label="Invoices overdue / unpaid" count={a.unpaid_invoices} color="#F43F5E" icon={Banknote} />
          <AlertRow to="/app/fleet" label="Fleet service due this week" count={a.fleet_due} color="#8B5CF6" icon={Truck} />
          <AlertRow to="/app/competencies" label="Certs expiring soon" count={a.certs_expiring} color="#B77905" icon={Award} />
        </div>
      </div>

      {/* Announcements */}
      <div className={panelCls} data-testid="panel-announcements">
        <PanelHeader icon={Megaphone} title="Announcements" />
        <div className="px-4 pb-4">
          <div className="max-h-36 overflow-y-auto space-y-2">
            {ann === null ? (
              <div className="h-8 bg-zinc-100 dark:bg-[#151C27] animate-pulse" />
            ) : ann.length === 0 ? (
              <p className="text-xs text-zinc-400 font-mono py-3">No announcements found</p>
            ) : ann.map((m) => (
              <div key={m.id} className="group flex items-start gap-2 rounded-lg bg-slate-50 dark:bg-white/[0.03] p-3" data-testid={`announcement-${m.id}`}>
                <span className="rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 p-2"><Megaphone className="w-4 h-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] leading-snug font-medium">{m.text}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="w-4.5 h-4.5 w-[18px] h-[18px] rounded-full bg-[#FF5F15] text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                      {(m.created_by_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                    </span>
                    <p className="text-[11px] font-semibold text-zinc-400">
                      {m.created_by_name} · {new Date(m.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}
                    </p>
                  </div>
                </div>
                <button onClick={() => removeAnn(m.id)} aria-label="Delete announcement" className="opacity-70 hover:opacity-100 focus-visible:opacity-100 text-zinc-500 hover:text-red-600 shrink-0" data-testid={`announcement-delete-${m.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-1.5 mt-2 pt-2 border-t border-zinc-200 dark:border-zinc-800">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && post()}
              placeholder="Post to the whole crew…"
              className="flex-1 min-w-0 h-8 px-2 text-xs bg-transparent border-2 border-zinc-300 dark:border-zinc-700 focus:border-[#FF5F15] outline-none transition-colors"
              data-testid="announcement-input"
            />
            <button onClick={post} disabled={posting || !draft.trim()}
              aria-label="Post announcement" className="h-8 px-3 rounded-md bg-[#FF5F15] text-white disabled:opacity-40 active:scale-95 transition-transform"
              data-testid="announcement-post-btn">
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Reminders */}
      <div className={panelCls} data-testid="panel-reminders">
        <PanelHeader
          icon={BellRing}
          title="Reminders"
          right={
            <button onClick={() => setShowAllReminders((v) => !v)} className="text-xs font-semibold text-[#FF5F15] hover:underline" data-testid="reminders-view-all">
              {showAllReminders ? "Show less" : "View all"}
            </button>
          }
        />
        <div className={`px-3 pb-3 overflow-y-auto ${showAllReminders ? "max-h-80" : "max-h-44"}`}>
          {panel === null ? (
            <div className="h-8 bg-zinc-100 dark:bg-[#151C27] animate-pulse" />
          ) : reminders.length === 0 ? (
            <p className="text-xs text-zinc-400 font-mono py-3">No reminders found</p>
          ) : (
            <div className="space-y-1">
              {reminders.map((r, i) => {
                const Icon = KIND_ICON[r.kind] || BellRing;
                return (
                  <Link key={i} to={r.link || "#"} className="flex items-center gap-2 px-1 py-1 hover:bg-orange-50 dark:hover:bg-[#151C27]/70 transition-colors" data-testid={`reminder-row-${i}`}>
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${r.overdue ? "text-red-600" : "text-[#FF5F15]"}`} />
                    <span className="text-xs truncate flex-1">{r.label}</span>
                    <span className={`text-[11px] font-mono font-bold whitespace-nowrap ${r.overdue ? "text-red-600" : "text-zinc-500"}`}>
                      {r.overdue ? "OVERDUE · " : ""}{fmtDate(r.date)}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
      </div>
      )}
    </>
  );
}

export function WelcomeBanner({ name, children }) {
  const { user } = useAuth();
  const dateStr = new Date().toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
  const actions = [
    { to: "/app/jobs/new", label: "New Job", icon: Hammer, tone: "orange", roles: ["admin", "office", "super_admin"] },
    { to: "/app/timesheets", label: "Add Timesheet", icon: Clock, tone: "green", roles: ["admin", "office", "super_admin", "field"] },
    { to: "/app/quotes/new", label: "Create Quote", icon: FileText, tone: "purple", roles: ["admin", "office", "super_admin", "quantity_surveyor"] },
    { to: "/app/materials", label: "Materials", icon: Boxes, tone: "blue", roles: ["admin", "office", "super_admin", "quantity_surveyor", "field"] },
    { to: "/app/crew-planner?view=day", label: "Plan Crew", icon: CalendarDays, tone: "blue", roles: ["admin", "office", "super_admin"] },
    { to: "/app/safety", label: "Log Hazard", icon: ShieldAlert, tone: "red" },
    { to: "/app/running-sheets", label: "Create Docket", icon: ClipboardCheck, tone: "purple", driver: true },
  ];
  return (
    <header className="fl-page-header overview-welcome" data-testid="welcome-banner">
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground mb-2">{dateStr}</p>
        <h1 className="fl-page-title">Welcome back, {name}</h1>
        <p className="fl-page-description">Here's what's happening today.</p>

      </div>
      <div className="overview-header-actions">
        <div className="overview-quick-actions">
          {actions.filter(a => (!a.roles || a.roles.includes(user.role)) && (!a.driver || user.truck_driver)).map(a => <Link key={a.label} to={a.to} className={`overview-action overview-tone-${a.tone}`} data-testid={`hero-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <a.icon className="w-6 h-6" strokeWidth={1.8} aria-hidden="true" /><span>{a.label}</span>
          </Link>)}
        </div>
      </div>
      <div className="overview-context-row"><FieldWeather variant="strip"/><div className="overview-header-tools">{children}</div></div>
    </header>
  );
}
